import { useCallback, useRef } from "react";
import { FormInstance } from "antd";
import { eAnswerType } from "../utils/enum";

interface UseFormAutoSaveProps {
  formId: string | null;
  form: FormInstance;
  stage: number;
  handleFieldChange: (
    field: string,
    value: any,
    answerType?: number,
    bulkPayload?: any[],
    isSubmitted?: boolean,
  ) => Promise<void>;
  excludeFields?: string[];
}

export const useFormAutoSave = ({
  formId,
  form,
  stage,
  handleFieldChange,
  excludeFields = [],
}: UseFormAutoSaveProps) => {
  // Debounce timeout refs for auto-save
  const debounceTimeouts = useRef<{ [key: string]: NodeJS.Timeout }>({});

  /**
   * Debounced save function - saves field after 1.5s of inactivity
   * More reliable than onBlur, especially on mobile
   */
  const debouncedSave = useCallback(
    (field: string, value: any, answerType: number = eAnswerType.TEXT) => {
      // Clear existing timeout for this field
      if (debounceTimeouts.current[field]) {
        clearTimeout(debounceTimeouts.current[field]);
      }

      // Set new timeout
      debounceTimeouts.current[field] = setTimeout(() => {
        handleFieldChange(field, value, answerType);
        delete debounceTimeouts.current[field];
      }, 1500);
    },
    [handleFieldChange],
  );

  /**
   * Detect answer type based on value
   */
  const detectAnswerType = (value: any): number => {
    if (value === null || value === undefined) return eAnswerType.TEXT;
    if (Array.isArray(value)) return eAnswerType.ARRAY;
    if (typeof value === "object" && value._isAMomentObject)
      return eAnswerType.DATE; // dayjs object check might need adjustment depending on dayjs version/usage
    // Check for dayjs object by properties if _isAMomentObject is not reliable (it's moment-specific)
    if (
      typeof value === "object" &&
      "$L" in value &&
      "$d" in value &&
      "$y" in value
    )
      return eAnswerType.DATE;
    if (typeof value === "object") return eAnswerType.OBJECT;
    return eAnswerType.TEXT;
  };

  /**
   * Format value based on answer type for API
   */
  const formatValueForApi = (value: any, answerType: number): string => {
    if (value === null || value === undefined) return "";

    /**
     * Helper function to recursively convert dayjs objects within arrays/objects
     * Formats month pickers as MM/YYYY and regular dates as YYYY-MM-DD
     */
    const convertDayjsInValue = (val: any): any => {
      if (val === null || val === undefined) return val;
      
      // Check if it's a dayjs object
      if (typeof val === "object" && "$L" in val && "$d" in val && "$y" in val) {
        // Format as MM/YYYY for month pickers (common in form arrays)
        return val.format("MM/YYYY");
      }
      
      // If it's an array, recursively process each item
      if (Array.isArray(val)) {
        return val.map(convertDayjsInValue);
      }
      
      // If it's an object, recursively process each property
      if (typeof val === "object") {
        const result: { [key: string]: any } = {};
        for (const key of Object.keys(val)) {
          result[key] = convertDayjsInValue(val[key]);
        }
        return result;
      }
      
      return val;
    };

    switch (answerType) {
      case eAnswerType.DATE:
        // dayjs object
        return value && value.format
          ? value.format("YYYY-MM-DD")
          : String(value);
      case eAnswerType.ARRAY:
      case eAnswerType.OBJECT:
        // Convert dayjs objects within arrays/objects before stringifying
        const convertedValue = convertDayjsInValue(value);
        return typeof convertedValue === "string" ? convertedValue : JSON.stringify(convertedValue);
      default:
        return String(value);
    }
  };

  /**
   * Handle form submit - sends ALL form values as safety net
   * This ensures no data is lost even if individual saves failed
   */
  const handleFormSubmit = async () => {
    // Cancel any pending debounced saves
    Object.values(debounceTimeouts.current).forEach(clearTimeout);
    debounceTimeouts.current = {};

    // Get all form values
    const allValues = form.getFieldsValue();

    // Convert to answers array format
    const answers: any[] = [];
    Object.entries(allValues).forEach(([key, value]) => {
      // Check exact match or if key starts with excluded field followed by underscore (for dynamic fields if any)
      // For now, exact match is safer based on previous implementation
      if (excludeFields.includes(key)) return;

      if (value === undefined || value === null || value === "") return;

      const answerType = detectAnswerType(value);
      const formattedValue = formatValueForApi(value, answerType);

      if (formattedValue) {
        answers.push({
          questionKey: key,
          answerType: answerType,
          answer: formattedValue,
          part: stage,
        });
      }
    });

    // Send bulk save with isSubmitted flag
    if (answers.length > 0) {
      await handleFieldChange("", "", eAnswerType.TEXT, answers, true);
    } else {
      await handleFieldChange("", "", eAnswerType.TEXT, undefined, true);
    }
  };

  /**
   * Handle form values change - centralized handler for all inputs
   * Eliminates the need for onBlur/onChange on individual fields
   */
  const onFormValuesChange = (changedValues: any, allValues: any) => {
    Object.keys(changedValues).forEach((key) => {
      // Handle excluded fields
      if (
        excludeFields.some(
          (field) => key === field || key.startsWith(`${field}_`),
        )
      )
        return;

      const value = allValues[key];
      const answerType = detectAnswerType(value);

      debouncedSave(key, value, answerType);
    });
  };

  return {
    debouncedSave,
    handleFormSubmit,
    onFormValuesChange,
  };
};
